import { z } from 'zod';
import type { Brand } from '../brand';

/**
 * ECONOMIC randomness — **contracts only, no implementation**.
 *
 * Gacha rolls, rare drops, purchasable rewards and anything convertible to Cash
 * must not be predictable, reproducible from client-visible data, or
 * re-rollable by restarting a session. The deterministic SplitMix64 generator
 * used for the hunt is unsuitable: its seed is public and its whole state is a
 * single 64-bit integer.
 *
 * ## Required properties (binding on the future implementation)
 *
 * 1. **Server-only entropy.** Drawn with Web Crypto (`crypto.getRandomValues`),
 *    available in the Cloudflare Workers runtime. Never derived from a request
 *    body, a client timestamp or a simulation seed.
 * 2. **Keyed derivation.** Where a value must be reproducible for audit, it is
 *    derived with HMAC-SHA-256 (`crypto.subtle`) under a secret held as a
 *    Workers secret — never in this repository, never in a bundle.
 * 3. **The secret never leaves the server.** Not in a response, not in a log,
 *    not in the audit record. Only {@link EconomicSecretKeyId} — an opaque key
 *    *identifier and version* — is ever persisted.
 * 4. **No seed reuse.** A {@link SimulationSeed} must never reach an economic
 *    draw. The nominal types in this file make that a compile error rather than
 *    a code-review question.
 * 5. **Not re-rollable.** The outcome is bound to the idempotency key of the
 *    request; replaying it returns the stored result instead of drawing again.
 * 6. **Auditable without exposing credentials.** The record below carries
 *    everything a dispute needs — event id, algorithm version, probability
 *    table version, nonce, result — and nothing that could be used to forge or
 *    predict a future draw.
 *
 * The concrete construction (commit-reveal? per-account chained nonce?) depends
 * on decisions that have not been made. This module therefore defines the
 * shapes and the guarantees, and stops there. Building half a cryptosystem
 * would be worse than building none.
 *
 * @see ./simulation for movement, spawns and the boss timer.
 */

/**
 * Server-generated randomness for one economic event, hex-encoded.
 *
 * Produced by `crypto.getRandomValues`. Minimum 128 bits.
 */
export type EconomicNonce = Brand<string, 'EconomicNonce'>;

/**
 * An **identifier** for the signing secret — never the secret itself.
 *
 * Persisting the key id (and rotating it) lets an auditor confirm which key was
 * in force for a given draw without ever handling key material.
 */
export type EconomicSecretKeyId = Brand<string, 'EconomicSecretKeyId'>;

/** Minimum entropy for an economic nonce, in bits. */
export const ECONOMIC_NONCE_MIN_BITS = 128;

const NONCE_MIN_HEX_CHARS = ECONOMIC_NONCE_MIN_BITS / 4;

export const economicNonceSchema = z
  .string()
  .regex(
    new RegExp(`^[0-9a-f]{${NONCE_MIN_HEX_CHARS},256}$`),
    `economic nonce must be lower-case hex carrying at least ${ECONOMIC_NONCE_MIN_BITS} bits`,
  )
  .transform((value) => value as EconomicNonce);

export const economicSecretKeyIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, 'secret key id must be an opaque, URL-safe label')
  .transform((value) => value as EconomicSecretKeyId);

/** Event families that require economic-grade randomness. */
export const ECONOMIC_RANDOMNESS_EVENTS = [
  'gacha_roll',
  'rare_drop',
  'purchasable_reward',
  'cash_linked_reward',
] as const;

export type EconomicRandomnessEvent = (typeof ECONOMIC_RANDOMNESS_EVENTS)[number];

export const economicRandomnessEventSchema = z.enum(ECONOMIC_RANDOMNESS_EVENTS);

/**
 * Versioning. Both are recorded with every draw so a historical outcome can be
 * re-evaluated against the exact rules that produced it, even after the odds
 * or the algorithm change.
 */
export const algorithmVersionSchema = z
  .string()
  .regex(/^v\d+(\.\d+)*$/, 'algorithm version must look like v1 or v2.3');

export const probabilityTableVersionSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9._-]*$/, 'probability table version must be an opaque label');

/**
 * What the server asks for when it needs an economic outcome.
 *
 * Note what is absent: any seed, any client-supplied entropy, any secret. The
 * implementation supplies its own entropy internally.
 */
export interface EconomicDrawRequest {
  /** Unique id of the event being resolved (one gacha roll, one drop). */
  readonly eventId: string;
  readonly event: EconomicRandomnessEvent;
  /** Ties the draw to the request; a replay returns the stored result. */
  readonly idempotencyKey: string;
  readonly probabilityTableVersion: string;
}

/**
 * What the server gets back.
 *
 * `bytes` is the raw randomness; interpreting it against a probability table is
 * a separate, table-versioned step that does not exist yet.
 */
export interface EconomicDrawResult {
  readonly eventId: string;
  readonly event: EconomicRandomnessEvent;
  readonly algorithmVersion: string;
  readonly probabilityTableVersion: string;
  readonly nonce: EconomicNonce;
  readonly secretKeyId: EconomicSecretKeyId;
  readonly bytes: Uint8Array;
  /** Server clock, ISO-8601 UTC. */
  readonly drawnAt: string;
}

/**
 * The seam a Workers-side implementation must satisfy.
 *
 * Intentionally async: `crypto.subtle` is promise-based, and forcing the async
 * shape now prevents a synchronous, non-cryptographic stand-in from ever
 * satisfying this interface by accident.
 */
export interface EconomicRandomnessSource {
  draw(request: EconomicDrawRequest): Promise<EconomicDrawResult>;
}

/**
 * Field names that must never appear anywhere in an audit record.
 *
 * Compared case-insensitively with separators stripped, so `master_seed`,
 * `masterSeed` and `MASTERSEED` are all caught.
 */
export const FORBIDDEN_AUDIT_FIELD_NAMES = [
  'secret',
  'mastersecret',
  'masterseed',
  'seed',
  'hmackey',
  'key',
  'privatekey',
  'signingkey',
  'password',
  'token',
  'apikey',
  'credential',
] as const;

function normalizeFieldName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Deep scan for secret-bearing field names.
 *
 * A belt-and-braces guard: the schema below refuses to validate a record that
 * carries key material, so an accidental `{ ...context, hmacKey }` spread fails
 * at the boundary instead of reaching permanent storage.
 */
export function containsForbiddenAuditField(value: unknown, depth = 0): boolean {
  if (depth > 8 || value === null || typeof value !== 'object') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((entry) => containsForbiddenAuditField(entry, depth + 1));
  }
  for (const [key, nested] of Object.entries(value)) {
    const normalized = normalizeFieldName(key);
    if ((FORBIDDEN_AUDIT_FIELD_NAMES as readonly string[]).includes(normalized)) {
      return true;
    }
    if (containsForbiddenAuditField(nested, depth + 1)) {
      return true;
    }
  }
  return false;
}

/**
 * The permanent audit record for one economic draw.
 *
 * Everything needed to re-examine a disputed outcome; nothing that could be
 * used to predict or forge the next one.
 */
export const economicDrawAuditRecordSchema = z
  .object({
    eventId: z.string().min(1).max(128),
    event: economicRandomnessEventSchema,
    algorithmVersion: algorithmVersionSchema,
    probabilityTableVersion: probabilityTableVersionSchema,
    nonce: economicNonceSchema,
    /** Key *identifier*, never key material. */
    secretKeyId: economicSecretKeyIdSchema,
    /** Hex-encoded outcome, already resolved against the probability table. */
    outcome: z.string().min(1).max(512),
    idempotencyKey: z.string().min(16).max(128),
    /** Server clock only. */
    drawnAt: z.iso.datetime(),
    /** Optional structured context; scanned for secret-bearing fields. */
    context: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((record) => !containsForbiddenAuditField(record.context), {
    message: 'audit record must not carry secret material',
    path: ['context'],
  });

export type EconomicDrawAuditRecord = z.infer<typeof economicDrawAuditRecordSchema>;
